import React, { useCallback, useEffect, useState } from 'react';
import { Check, Image as ImageIcon, Film } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { focalPosition } from '../lib/photoFocal';
import { toast } from 'sonner';

// SlideshowPhotoPicker — an inline grid of all PUBLIC photos on the
// memorial so the family can pick which ones appear in the Photo
// Slideshow video BEFORE regenerating. Clicking a thumbnail toggles
// `include_in_slideshow` on that media row. Empty selection falls back
// to "all public photos" on the backend (routes_video.py).
//
// iter94.8 — Surfaced more prominently in the VideoTab: explicit heading,
// helper copy, and a "What will play" preview strip showing the order
// the slideshow renderer will use.

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function SlideshowPhotoPicker({ memorialId }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/memorials/${memorialId}/media?visibility=public`,
      );
      const images = (data.media || []).filter((m) => m.kind !== 'video');
      setPhotos(images);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [memorialId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (m) => {
    const next = !m.include_in_slideshow;
    setBusyId(m.media_id);
    // Optimistic update
    setPhotos((prev) =>
      prev.map((p) => (p.media_id === m.media_id ? { ...p, include_in_slideshow: next } : p)),
    );
    try {
      await api.patch(`/media/${m.media_id}`, { include_in_slideshow: next });
    } catch (e) {
      toast.error(formatApiError(e));
      // Roll back
      setPhotos((prev) =>
        prev.map((p) =>
          p.media_id === m.media_id ? { ...p, include_in_slideshow: !next } : p,
        ),
      );
    } finally {
      setBusyId(null);
    }
  };

  const selectAll = async (value) => {
    // Apply to all currently-displayed photos.
    const targets = photos.filter((p) => !!p.include_in_slideshow !== value);
    if (targets.length === 0) return;
    // Optimistic
    setPhotos((prev) => prev.map((p) => ({ ...p, include_in_slideshow: value })));
    try {
      await Promise.all(
        targets.map((m) =>
          api.patch(`/media/${m.media_id}`, { include_in_slideshow: value }),
        ),
      );
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  if (loading) {
    return (
      <div className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.5)]">
        Loading photos…
      </div>
    );
  }

  const selectedCount = photos.filter((p) => p.include_in_slideshow).length;
  const total = photos.length;

  if (total === 0) {
    return (
      <div
        data-testid="slideshow-photos-empty"
        className="rounded-md border border-dashed border-[hsl(var(--primary)/0.15)] bg-[hsl(var(--background))] px-4 py-6 text-center"
      >
        <ImageIcon
          size={18}
          className="mx-auto text-[hsl(var(--primary)/0.35)]"
          strokeWidth={1.5}
        />
        <p className="mt-2 text-[13px] text-[hsl(var(--primary)/0.6)]">
          No public photos yet. Upload a few on the Memories tab — they'll appear here to pick for the slideshow.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="slideshow-photo-picker"
      className="space-y-4 rounded-xl border border-[hsl(var(--primary)/0.14)] bg-[hsl(var(--background))] p-5"
    >
      {/* iter94.8 — Explicit, friendly header so the picker isn't lost
          inside the Video tab. */}
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-[hsl(var(--accent)/0.12)] text-[hsl(var(--accent))] flex items-center justify-center shrink-0">
          <Film size={16} strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--primary)/0.7)]">
            Step 1
          </div>
          <h4 className="font-serif text-lg text-[hsl(var(--primary))] mt-0.5">
            Choose photos for the slideshow
          </h4>
          <p className="text-[13px] text-[hsl(var(--primary)/0.75)] mt-1 leading-relaxed">
            Tap any photo below to add it to the slideshow video. The selected photos will play in the order they were uploaded, each lingering for about 3-4 seconds.
            {selectedCount === 0 && (
              <> If you don't pick any, all of your public photos will play automatically.</>
            )}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
        <div className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.65)]">
          In the slideshow ·{' '}
          <span
            className="text-[hsl(var(--primary))] font-semibold"
            data-testid="slideshow-photo-count"
          >
            {selectedCount || 'all'} of {total}
          </span>
        </div>
        <div className="flex gap-4 text-[10px] tracking-[0.18em] uppercase">
          <button
            type="button"
            onClick={() => selectAll(true)}
            disabled={selectedCount === total}
            data-testid="slideshow-select-all"
            className="text-[hsl(var(--primary)/0.7)] hover:text-[hsl(var(--primary))] hover:underline disabled:opacity-40 disabled:no-underline"
          >
            Select all
          </button>
          <button
            type="button"
            onClick={() => selectAll(false)}
            disabled={selectedCount === 0}
            data-testid="slideshow-select-none"
            className="text-[hsl(var(--primary)/0.7)] hover:text-[hsl(var(--primary))] hover:underline disabled:opacity-40 disabled:no-underline"
          >
            Clear selection
          </button>
        </div>
      </div>

      <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
        {photos.map((m) => {
          const url =
            m.external_url ||
            `${BACKEND}/api/media/${m.media_id}/file`;
          const on = !!m.include_in_slideshow;
          return (
            <button
              key={m.media_id}
              type="button"
              onClick={() => toggle(m)}
              disabled={busyId === m.media_id}
              aria-pressed={on}
              data-testid={`slideshow-photo-${m.media_id}`}
              className={`relative aspect-square rounded-md overflow-hidden border transition-all focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))]/50 ${
                on
                  ? 'border-[hsl(var(--accent))] ring-1 ring-[hsl(var(--accent))]/40'
                  : 'border-[hsl(var(--primary)/0.15)] hover:border-[hsl(var(--primary)/0.35)]'
              }`}
              style={{
                boxShadow: on
                  ? '0 6px 16px -10px rgba(197,160,89,0.55)'
                  : 'inset 0 0 0 1px rgba(255,255,255,0.4)',
              }}
            >
              <img
                src={url}
                alt=""
                style={{ objectPosition: focalPosition(m) }}
                className={`w-full h-full object-cover transition-all ${
                  on ? '' : 'opacity-55 grayscale-[35%]'
                }`}
                loading="lazy"
              />
              {/* Check badge */}
              <span
                className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-full border flex items-center justify-center transition-all ${
                  on
                    ? 'bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-white'
                    : 'bg-[hsl(var(--background))]/85 border-[hsl(var(--primary)/0.3)] text-transparent'
                }`}
                aria-hidden
              >
                <Check size={11} strokeWidth={3} />
              </span>
            </button>
          );
        })}
      </div>

      {/* iter94.8 — "What will play" preview strip. Mirrors the order
          the backend slideshow renderer uses (sorted by created_at).
          Hidden when the user hasn't picked anything (the helper copy
          above already says "all of your public photos will play"). */}
      {selectedCount > 0 && (
        <div className="pt-1" data-testid="slideshow-preview-strip">
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--primary)/0.6)] mb-2">
            What will play, in order
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {photos
              .filter((p) => p.include_in_slideshow)
              .map((m, idx) => {
                const url =
                  m.external_url ||
                  `${BACKEND}/api/media/${m.media_id}/file`;
                return (
                  <div
                    key={`prev-${m.media_id}`}
                    className="relative w-16 h-16 shrink-0 rounded-md overflow-hidden border border-[hsl(var(--accent)/0.4)]"
                  >
                    <img src={url} alt="" className="w-full h-full object-cover" style={{ objectPosition: focalPosition(m) }} loading="lazy" />
                    <span className="absolute bottom-0 left-0 text-[10px] font-semibold bg-[hsl(var(--accent))] text-white px-1.5 py-0.5 rounded-tr-md">
                      {idx + 1}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      <p className="text-[11px] text-[hsl(var(--primary)/0.55)] italic leading-relaxed pt-1 border-t border-[hsl(var(--primary)/0.08)]">
        <span className="font-medium not-italic">Step 2:</span> after you're happy with the photos above, hit the <span className="font-medium not-italic">Regenerate</span> button below to build a fresh video.
      </p>
    </div>
  );
}

export default SlideshowPhotoPicker;
